<script setup>
/* global axios */
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  entityType: { type: String, required: true }, // 'contact' or 'company'
  entityId: { type: [Number, String], required: true },
});

const route = useRoute();
const accountId = computed(() => route.params.accountId);

const deals = ref([]);
const isLoading = ref(false);

const fetchDeals = async () => {
  if (!props.entityId || !accountId.value) return;
  isLoading.value = true;
  try {
    const basePath = `/api/v1/accounts/${accountId.value}`;
    const url =
      props.entityType === 'company'
        ? `${basePath}/companies/${props.entityId}/deals`
        : `${basePath}/contacts/${props.entityId}/deals`;
    const response = await axios.get(url);
    deals.value = response.data?.payload || [];
  } catch {
    deals.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Summary computations
const wonDeals = computed(() => deals.value.filter(d => d.status === 'won'));
const openDeals = computed(() => deals.value.filter(d => d.status === 'open'));

const totalContractValue = computed(() =>
  wonDeals.value.reduce((sum, d) => sum + (d.total_value || 0), 0)
);

const totalRecurring = computed(() =>
  wonDeals.value.reduce((sum, d) => sum + (d.recurring_value || 0), 0)
);

const totalMRR = computed(() =>
  wonDeals.value.reduce((sum, d) => sum + (d.mrr || 0), 0)
);

const isRecurring = computed(
  () => totalRecurring.value > 0 || totalMRR.value > 0
);

const openPipelineValue = computed(() =>
  openDeals.value.reduce((sum, d) => sum + (d.total_value || 0), 0)
);

const nextCloseDate = computed(() => {
  const dates = openDeals.value
    .map(d => d.expected_close_date)
    .filter(Boolean)
    .sort();
  return dates.length > 0 ? dates[0] : null;
});

const hasData = computed(
  () =>
    !isLoading.value &&
    deals.value.length > 0 &&
    (wonDeals.value.length > 0 || openDeals.value.length > 0)
);

const formatCurrency = value => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatDate = dateStr => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

watch(
  () => props.entityId,
  newId => {
    if (newId) fetchDeals();
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-if="hasData"
    class="rounded-xl border border-n-weak bg-white dark:bg-n-solid-2 overflow-hidden"
  >
    <div
      class="px-4 py-3 border-b border-n-weak bg-n-slate-2"
    >
      <div class="flex items-center gap-2">
        <span
          class="i-lucide-file-text size-4 text-n-slate-11"
        />
        <h4
          class="text-xs font-semibold uppercase tracking-wider text-n-slate-11"
        >
          Resumo de Contratos
        </h4>
      </div>
    </div>

    <div
      class="grid grid-cols-2 md:grid-cols-4 gap-px bg-n-slate-3"
    >
      <!-- Total contract value (won) -->
      <div class="bg-white dark:bg-n-solid-2 px-4 py-3">
        <p
          class="text-[10px] font-medium uppercase tracking-wider text-n-slate-9 mb-1"
        >
          Valor Contratado
        </p>
        <p
          class="text-base font-bold text-green-600 dark:text-green-400 tabular-nums"
        >
          {{ formatCurrency(totalContractValue) }}
        </p>
        <p class="text-[10px] text-n-slate-9 mt-0.5">
          {{ wonDeals.length }} contrato(s) ganho(s)
        </p>
      </div>

      <!-- Recurrence -->
      <div class="bg-white dark:bg-n-solid-2 px-4 py-3">
        <p
          class="text-[10px] font-medium uppercase tracking-wider text-n-slate-9 mb-1"
        >
          Recorrencia
        </p>
        <template v-if="isRecurring">
          <p
            class="text-base font-bold text-blue-600 dark:text-blue-400 tabular-nums"
          >
            {{ formatCurrency(totalMRR) }}/mes
          </p>
          <p class="text-[10px] text-n-slate-9 mt-0.5">Receita recorrente</p>
        </template>
        <template v-else>
          <p class="text-base font-bold text-n-slate-11">
            Avulso
          </p>
          <p class="text-[10px] text-n-slate-9 mt-0.5">Sem recorrencia</p>
        </template>
      </div>

      <!-- Open pipeline value -->
      <div class="bg-white dark:bg-n-solid-2 px-4 py-3">
        <p
          class="text-[10px] font-medium uppercase tracking-wider text-n-slate-9 mb-1"
        >
          Pipeline Aberto
        </p>
        <p
          class="text-base font-bold text-amber-600 dark:text-amber-400 tabular-nums"
        >
          {{ formatCurrency(openPipelineValue) }}
        </p>
        <p class="text-[10px] text-n-slate-9 mt-0.5">
          {{ openDeals.length }} deal(s) em aberto
        </p>
      </div>

      <!-- Next close date -->
      <div class="bg-white dark:bg-n-solid-2 px-4 py-3">
        <p
          class="text-[10px] font-medium uppercase tracking-wider text-n-slate-9 mb-1"
        >
          Proxima Previsao
        </p>
        <p
          v-if="nextCloseDate"
          class="text-base font-bold text-n-slate-12"
        >
          {{ formatDate(nextCloseDate) }}
        </p>
        <p
          v-else
          class="text-base font-bold text-n-slate-9"
        >
          &mdash;
        </p>
        <p class="text-[10px] text-n-slate-9 mt-0.5">Fechamento estimado</p>
      </div>
    </div>
  </div>
</template>
